import React, { useCallback, useEffect, useState } from 'react';
import authService from '../../../services/authService';
import { API_ROOT } from '../../api';
import { StatCard } from '../../shared/StatCard';

async function authHeaders() {
  const idToken = await authService.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`
  };
}

const ChatbotDocumentsTab = () => {
  const [indexInfo, setIndexInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchError, setSearchError] = useState('');

  const loadIndex = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_ROOT}/doc-search`, {
        headers: await authHeaders()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Failed to load index (${response.status})`);
      }
      setIndexInfo(data);
    } catch (err) {
      setError(err.message || 'Failed to load Databricks AI Search index');
      setIndexInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIndex();
  }, [loadIndex]);

  const handleSearch = async (event) => {
    event.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setSearchError('');
    try {
      const response = await fetch(`${API_ROOT}/doc-search`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ query: query.trim(), n_results: 5 })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Search failed (${response.status})`);
      }
      setSearchResults(Array.isArray(data.results) ? data.results : []);
    } catch (err) {
      setSearchError(err.message || 'Search failed');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const status = indexInfo?.status || {};
  const documents = Array.isArray(indexInfo?.documents) ? indexInfo.documents : [];
  const stateLabel = status.detailedState || (status.ready ? 'Online' : (loading ? 'Loading' : 'Unknown'));

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Chatbot Documents</h2>
          <p className="text-sm text-gray-600">
            Knowledge base is served by Databricks AI Search, not the previous Lightsail document API.
          </p>
        </div>
        <button
          onClick={loadIndex}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded flex items-center"
        >
          <i className={`fa-solid fa-sync-alt mr-2 ${loading ? 'animate-spin' : ''}`}></i>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="Index status" value={stateLabel} icon="fa-signal" color={status.ready === false ? 'red' : 'green'} />
        <StatCard title="Rows indexed" value={status.indexedRowCount != null ? String(status.indexedRowCount) : (loading ? 'Loading...' : '-')} icon="fa-layer-group" color="blue" />
        <StatCard title="Endpoint" value={indexInfo?.endpointName || 'thrive360-doc-search'} icon="fa-server" color="indigo" />
        <StatCard title="Source" value="Databricks AI Search" icon="fa-database" color="purple" />
      </div>

      <div className="bg-white border rounded-lg p-4 mb-6 text-sm text-gray-700 space-y-2">
        <div><span className="font-semibold">Index:</span> {indexInfo?.indexName || 'che_gpd_thrive360.thrive360_doc_search.thrive360_index'}</div>
        <div><span className="font-semibold">Source table:</span> {indexInfo?.sourceTable || 'che_gpd_thrive360.thrive360_doc_search.thrive_360_docs'}</div>
        <div className="flex flex-wrap gap-4">
          {indexInfo?.sourceTableUrl && (
            <a className="text-blue-600 hover:underline" href={indexInfo.sourceTableUrl} target="_blank" rel="noreferrer">
              Open source table in Databricks
            </a>
          )}
          {indexInfo?.catalogUrl && (
            <a className="text-blue-600 hover:underline" href={indexInfo.catalogUrl} target="_blank" rel="noreferrer">
              Open search index in Databricks
            </a>
          )}
        </div>
      </div>

      <div className="bg-white border rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Test document search</h3>
        <form onSubmit={handleSearch} className="flex gap-2 mb-4">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the indexed documents..."
            className="flex-1 border border-gray-300 rounded px-3 py-2"
          />
          <button
            type="submit"
            disabled={searching || !query.trim()}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded"
          >
            {searching ? 'Searching...' : 'Search'}
          </button>
        </form>
        {searchError && <p className="text-sm text-red-600 mb-3">{searchError}</p>}
        <div className="space-y-3">
          {searchResults.map((result, idx) => (
            <div key={idx} className="border rounded p-3 bg-gray-50">
              <div className="text-sm font-semibold text-gray-800 mb-1">
                {result?.metadata?.filename || 'Untitled chunk'}
                {result?.score != null && (
                  <span className="ml-2 font-normal text-gray-500">score {Number(result.score).toFixed(3)}</span>
                )}
              </div>
              <pre className="text-xs text-gray-700 whitespace-pre-wrap">{result.content}</pre>
            </div>
          ))}
          {!searching && searchResults.length === 0 && query && !searchError && (
            <p className="text-sm text-gray-500">No matching chunks returned.</p>
          )}
        </div>
      </div>

      {documents.length > 0 && (
        <div className="bg-white border rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Indexed documents</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chunks</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {documents.map((doc) => (
                  <tr key={doc.filename}>
                    <td className="px-6 py-4 text-sm text-gray-900">{doc.filename}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{doc.chunkCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotDocumentsTab;
